import { useEffect, useState } from "react";

/**
 * Tracks the user's `prefers-reduced-motion` setting.
 *
 * **First-paint default is `true` (R-RM-2)**: the first render always
 * reports reduced motion, so any consumer can render the safe static
 * variant on the initial paint. The effect re-evaluates
 * `window.matchMedia` on mount and subscribes to subsequent changes
 * (e.g. when the user toggles the OS-level setting live).
 *
 * For users who do NOT prefer reduced motion, the effect re-evaluates
 * synchronously after the first commit; the re-render with `false`
 * happens within the first animation frame, so the user perceives at
 * most a one-frame (~16ms) flash of the static variant before the
 * motion variant replaces it.
 */
export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(true);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const handler = (event: MediaQueryListEvent) => setReduced(event.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  return reduced;
}
